import {X} from "lucide-react";
import {type ReactNode, useEffect, useRef} from "react";

type ModalSize = "sm" | "md" | "lg" | "xl";

interface BaseModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: ModalSize;
  footer?: ReactNode;
}

const sizeStyles: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
};

export const BaseModal = ({open, onClose, title, children, size = "md", footer}: BaseModalProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-fade-in"
    >
      <div
        className={[
          "relative bg-(--color-bg-card) rounded-xl shadow-xl w-full flex flex-col max-h-[90vh]",
          sizeStyles[size],
        ].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-border)">
            <h2 className="text-lg font-semibold text-(--color-text-primary)">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-md text-(--color-text-tertiary) hover:text-(--color-text-primary) hover:bg-(--color-surface-2) transition-colors"
              aria-label="Cerrar"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-(--color-border) flex items-center justify-end gap-3">{footer}</div>
        )}
      </div>
    </div>
  );
};
